import { BanknoteArrowDown, CircleCheck, Landmark, Truck } from "lucide-react";
import { useState } from "react";

import type { TransactionList } from "@/client";
import { MetricCard } from "@/components/MetricCard";
import { Checks } from "@/domain/checks";
import { SupplierPaymentsView } from "@/features/dashboard/SupplierPaymentsView";
import { FinancePresentation } from "@/presentation/finance";

type FreightDirection = "INBOUND" | "OUTBOUND";
type OutboundFreightMovement = ReturnType<typeof Checks.list>[number];

export function FreightsView({ data }: { data: TransactionList }) {
	const [direction, setDirection] = useState<FreightDirection>("OUTBOUND");

	return (
		<>
			<nav
				aria-label="Tipos de frete"
				className="mb-6 inline-flex rounded-xl border border-stone-200 bg-white p-1 shadow-sm"
			>
				<FreightTab active={direction === "INBOUND"} onClick={() => setDirection("INBOUND")}>
					Frete de entrada
				</FreightTab>
				<FreightTab active={direction === "OUTBOUND"} onClick={() => setDirection("OUTBOUND")}>
					Frete de saída
				</FreightTab>
			</nav>

			{direction === "INBOUND" ? (
				<SupplierPaymentsView data={data} categoryId="inbound_freight" />
			) : (
				<OutboundFreightView data={data} />
			)}
		</>
	);
}

function OutboundFreightView({ data }: { data: TransactionList }) {
	const movements = Checks.list(data.transactions).filter(
		(movement) => movement.checkDestination?.destinationId === "OUTBOUND_FREIGHT",
	);
	const compensated = movements.filter((movement) => movement.kind === "COMPENSATED");
	const cashWithdrawals = movements.filter((movement) => movement.kind === "CASH_WITHDRAWAL");
	const groups = Checks.groupByDate(movements);
	const totalCents = totalAmount(movements);

	return (
		<>
			<section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
				<MetricCard
					label="Fretes de saída pagos"
					value={FinancePresentation.money(totalCents)}
					detail={`${movements.length} pagamentos por cheque`}
				/>
				<MetricCard
					label="Conciliados"
					value={`${movements.length}/${movements.length}`}
					detail="movimento bancário + destino confirmado"
					tone="success"
				/>
				<MetricCard
					label="Cheques compensados"
					value={FinancePresentation.money(totalAmount(compensated))}
					detail={`${compensated.length} cheques`}
				/>
				<MetricCard
					label="Sacados por terceiros"
					value={FinancePresentation.money(totalAmount(cashWithdrawals))}
					detail={`${cashWithdrawals.length} cheques que continuam sendo fretes`}
				/>
			</section>

			<section className="mb-6 rounded-3xl border border-emerald-900/10 bg-emerald-950 p-6 text-white shadow-sm">
				<div className="flex items-start gap-4">
					<span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-white/10">
						<Truck className="size-5" />
					</span>
					<div>
						<h2 className="font-semibold">Conciliação financeira concluída</h2>
						<p className="mt-1 max-w-4xl text-sm leading-6 text-emerald-50/75">
							Cada saída abaixo liga um movimento bancário efetivado ao número do cheque e ao
							destino confirmado “Frete de saída — CIF”. Motorista, CT-e, cliente e entrega
							continuam como alocações operacionais separadas para a próxima etapa.
						</p>
					</div>
				</div>
			</section>

			<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="flex items-center justify-between border-b border-stone-200 px-5 py-5">
					<div>
						<h2 className="font-semibold text-stone-950">Pagamentos conciliados por data</h2>
						<p className="mt-1 text-xs text-stone-500">
							A correção do destino permanece disponível na área Cheques.
						</p>
					</div>
					<span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800">
						{movements.length} conciliados
					</span>
				</header>

				{groups.map((group) => (
					<section key={group.date}>
						<header className="flex items-center justify-between border-b border-stone-100 bg-stone-50/80 px-5 py-3">
							<div>
								<h3 className="text-sm font-semibold text-stone-800">
									{FinancePresentation.transactionDate(group.date)}
								</h3>
								<p className="mt-0.5 text-[11px] text-stone-500">
									{group.count} {group.count === 1 ? "pagamento" : "pagamentos"}
								</p>
							</div>
							<p className="text-sm font-semibold text-stone-900">
								{FinancePresentation.money(group.amountCents)}
							</p>
						</header>
						<div className="divide-y divide-stone-100">
							{group.items.map((movement) => (
								<OutboundFreightRow key={movement.id} movement={movement} />
							))}
						</div>
					</section>
				))}
			</section>
		</>
	);
}

function OutboundFreightRow({ movement }: { movement: OutboundFreightMovement }) {
	return (
		<article className="grid gap-3 px-5 py-4 transition hover:bg-stone-50/70 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center">
			<div className="min-w-0">
				<p className="font-semibold text-stone-950">Cheque nº {movement.checkNumber}</p>
				<p className="mt-1 truncate text-xs text-stone-500">{movement.description}</p>
			</div>
			<div className="flex flex-wrap items-center gap-2">
				<span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-800">
					{movement.kind === "CASH_WITHDRAWAL" ? (
						<BanknoteArrowDown className="size-3.5" />
					) : (
						<Landmark className="size-3.5" />
					)}
					{movement.kind === "CASH_WITHDRAWAL" ? "Saque por cheque" : "Cheque compensado"}
				</span>
				<span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
					<CircleCheck className="size-3.5" />
					Conciliado
				</span>
			</div>
			<p className="text-lg font-semibold tracking-[-0.03em] text-stone-950 sm:text-right">
				{FinancePresentation.money(movement.amountCents)}
			</p>
		</article>
	);
}

function FreightTab({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition ${active ? "bg-emerald-950 text-white shadow-sm" : "text-stone-500 hover:bg-stone-50 hover:text-stone-900"}`}
		>
			{children}
		</button>
	);
}

function totalAmount(movements: OutboundFreightMovement[]) {
	return movements.reduce((total, movement) => total + movement.amountCents, 0);
}
